// src/Views/Admin/AdminDashboard.js
import React, { useEffect, useState } from 'react';
import { getFirestore, collection, getDocs, Timestamp } from 'firebase/firestore';
import { app } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend
} from 'recharts';
import { format, subDays, isAfter } from 'date-fns';
import './AdminDashboard.css';

const db = getFirestore(app);

// 🔒 SECURITY CONFIGURATION
const ADMIN_ACCESS_CODE = "AiPredict2025"; 

const AdminDashboard = () => {
    // eslint-disable-next-line no-unused-vars
    const { user } = useAuth(); 
    const navigate = useNavigate();
    
    // --- GATEKEEPER STATE ---
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [inputCode, setInputCode] = useState('');
    const [authError, setAuthError] = useState('');

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(''); // NEW: Error state
    
    // Stats State
    const [stats, setStats] = useState({
        total: 0,
        newUsers: 0,
        activeUsers: 0,
        premiumUsers: 0
    });

    const [graphData, setGraphData] = useState([]);

    // Shared Date Constants
    const now = new Date();
    const twoDaysAgo = subDays(now, 2);

    // 1. CHECK SESSION ON MOUNT
    useEffect(() => {
        const sessionAuth = sessionStorage.getItem('admin_unlocked');
        if (sessionAuth === 'true') {
            setIsAuthorized(true);
            // We call fetch immediately if already unlocked
        }
    }, []);

    // Trigger fetch when authorized becomes true
    useEffect(() => {
        if (isAuthorized) {
            fetchTelemetry();
        }
    }, [isAuthorized]);

    // 2. FETCH DATA FUNCTION (Enhanced Debugging)
    const fetchTelemetry = async () => {
        setLoading(true);
        setFetchError('');
        try {
            console.log("Admin: Attempting to fetch users collection...");
            const querySnapshot = await getDocs(collection(db, "users"));
            
            console.log(`Admin: Fetched ${querySnapshot.docs.length} user documents.`);

            const userList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setUsers(userList);
            processStats(userList);
        } catch (error) {
            console.error("CRITICAL ADMIN ERROR:", error);
            setFetchError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // 3. PROCESS DATA LOGIC (Safety Checks Added)
    const processStats = (data) => {
        const processNow = new Date();
        const sevenDaysAgo = subDays(processNow, 7);
        const processTwoDaysAgo = subDays(processNow, 2);

        let newCount = 0;
        let activeCount = 0;
        let premiumCount = 0;

        const dateMap = {};
        for (let i = 6; i >= 0; i--) {
            const d = subDays(processNow, i);
            const key = format(d, 'MM/dd');
            dateMap[key] = 0;
        }

        data.forEach(u => {
            // Count Premium
            let isTrial = false;
            // Handle Timestamp objects safely
            const trialEnd = u.trialEndsAt instanceof Timestamp ? u.trialEndsAt.toDate() : (u.trialEndsAt ? new Date(u.trialEndsAt) : null);
            
            if (u.trialActive && trialEnd && trialEnd > processNow) {
                isTrial = true;
            }
            if (u.isPremium || isTrial) premiumCount++;

            // Count New
            let createdDate = null;
            if (u.createdAt) {
                createdDate = u.createdAt instanceof Timestamp ? u.createdAt.toDate() : new Date(u.createdAt);
                
                // Only count if date is valid
                if (!isNaN(createdDate)) {
                    if (isAfter(createdDate, sevenDaysAgo)) {
                        newCount++;
                    }
                    const dateKey = format(createdDate, 'MM/dd');
                    if (dateMap[dateKey] !== undefined) dateMap[dateKey]++;
                }
            }

            // Count Active
            if (u.lastSeenAt) {
                const seenDate = u.lastSeenAt instanceof Timestamp ? u.lastSeenAt.toDate() : new Date(u.lastSeenAt);
                if (!isNaN(seenDate) && isAfter(seenDate, processTwoDaysAgo)) activeCount++;
            }
        });

        setStats({
            total: data.length,
            newUsers: newCount,
            activeUsers: activeCount,
            premiumUsers: premiumCount
        });

        const chartData = Object.keys(dateMap).map(key => ({
            date: key,
            users: dateMap[key]
        }));
        setGraphData(chartData);
    };

    // 4. HANDLE UNLOCK ATTEMPT
    const handleUnlock = (e) => {
        e.preventDefault();
        if (inputCode === ADMIN_ACCESS_CODE) {
            setIsAuthorized(true);
            sessionStorage.setItem('admin_unlocked', 'true');
            setAuthError('');
        } else {
            setAuthError('Access Denied: Invalid Security Code');
            setInputCode('');
        }
    };

    const pieData = [
        { name: 'Active (48h)', value: stats.activeUsers },
        { name: 'Inactive', value: stats.total - stats.activeUsers },
    ];
    const COLORS = ['#4ade80', '#1e293b']; 


    // --- 🔒 LOCK SCREEN VIEW ---
    if (!isAuthorized) {
        return (
            <div className="admin-lock">
                <div className="admin-lock__card">
                    <div className="admin-lock__icon">
                        <Icon icon="solar:shield-warning-bold" />
                    </div>
                    <h2 className="admin-lock__title">Restricted Access</h2>
                    <p className="admin-lock__text">Enter security clearance code to proceed.</p>
                    
                    <form onSubmit={handleUnlock} className="admin-lock__form">
                        <input 
                            type="password" 
                            className="admin-lock__input"
                            placeholder="Enter Code..."
                            value={inputCode}
                            onChange={(e) => setInputCode(e.target.value)}
                            autoFocus
                        />
                        <button type="submit" className="admin-lock__btn">
                            Unlock System
                        </button>
                    </form>
                    
                    {authError && <div className="admin-lock__error">{authError}</div>}
                    
                    <button className="admin-lock__back" onClick={() => navigate('/')}>
                        Return to Safety
                    </button>
                </div>
            </div>
        );
    }

    // --- 🔓 DASHBOARD VIEW ---
    if (loading) return <div className="admin-loading">Initializing Telemetry...</div>;

    return (
        <div className="admin">
            <header className="admin__header">
                <h1 className="admin__title">Platform Telemetry</h1>
                <button className="admin__close" onClick={() => navigate('/dashboard')}>
                    Exit <Icon icon="solar:logout-2-linear" />
                </button>
            </header>

            {/* ERROR BANNER */}
            {fetchError && (
                <div style={{background: '#ef444420', color:'#ef4444', padding:'1rem', marginBottom:'1rem', borderRadius:'8px', border:'1px solid #ef4444'}}>
                    <strong>Data Fetch Error:</strong> {fetchError} <br/>
                    <small>Check Firestore Rules or Console Logs.</small>
                </div>
            )}

            {/* STAT CARDS */}
            <div className="admin__grid">
                <div className="admin__card">
                    <div className="admin__icon-box admin__icon-box--blue">
                        <Icon icon="solar:users-group-rounded-bold" />
                    </div>
                    <div>
                        <p className="admin__label">Total Users</p>
                        <h2 className="admin__value">{stats.total}</h2>
                    </div>
                </div>

                <div className="admin__card">
                    <div className="admin__icon-box admin__icon-box--green">
                        <Icon icon="solar:graph-new-bold" />
                    </div>
                    <div>
                        <p className="admin__label">New (7 Days)</p>
                        <h2 className="admin__value">+{stats.newUsers}</h2>
                    </div>
                </div>

                <div className="admin__card">
                    <div className="admin__icon-box admin__icon-box--purple">
                        <Icon icon="solar:pulse-bold" />
                    </div>
                    <div>
                        <p className="admin__label">Active (48h)</p>
                        <h2 className="admin__value">{stats.activeUsers}</h2>
                    </div>
                </div>

                <div className="admin__card">
                    <div className="admin__icon-box admin__icon-box--gold">
                        <Icon icon="solar:crown-star-bold" />
                    </div>
                    <div>
                        <p className="admin__label">Premium / Trial</p>
                        <h2 className="admin__value">{stats.premiumUsers}</h2>
                    </div>
                </div>
            </div>

            {/* GRAPHS */}
            <div className="admin__charts">
                <div className="admin__chart-container">
                    <h3 className="admin__chart-title">User Growth (Last 7 Days)</h3>
                    <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                            <LineChart data={graphData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                <XAxis dataKey="date" stroke="#94a3b8" />
                                <YAxis stroke="#94a3b8" />
                                <Tooltip contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', color: '#fff' }} />
                                <Line type="monotone" dataKey="users" stroke="#38bdf8" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 8 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="admin__chart-container">
                    <h3 className="admin__chart-title">System Activity Ratio</h3>
                    <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '8px' }} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* TABLE */}
            <div className="admin__table-wrap">
                <h3 className="admin__chart-title">Recent Signups</h3>
                <table className="admin__table">
                    <thead>
                        <tr>
                            <th>User Email</th>
                            <th>Status</th>
                            <th>Joined</th>
                            <th>Last Seen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.sort((a,b) => {
                            const dateA = a.createdAt instanceof Timestamp ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
                            const dateB = b.createdAt instanceof Timestamp ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
                            return dateB - dateA;
                        }).slice(0, 10).map(u => {
                            const lastSeenDate = u.lastSeenAt instanceof Timestamp ? u.lastSeenAt.toDate() : (u.lastSeenAt ? new Date(u.lastSeenAt) : null);
                            const isActiveRecent = lastSeenDate && isAfter(lastSeenDate, twoDaysAgo);

                            return (
                                <tr key={u.id}>
                                    <td>{u.email || 'No Email'}</td>
                                    <td>
                                        {u.isPremium ? <span className="badge badge--prem">Premium</span> : 
                                         u.trialActive ? <span className="badge badge--trial">Trial</span> : 
                                         <span className="badge badge--free">Free</span>}
                                    </td>
                                    <td>
                                        {u.createdAt instanceof Timestamp ? format(u.createdAt.toDate(), 'MMM dd') : 'N/A'}
                                    </td>
                                    <td style={{color: isActiveRecent ? '#4ade80' : '#94a3b8'}}>
                                        {lastSeenDate ? format(lastSeenDate, 'HH:mm MMM dd') : 'Never'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminDashboard;



